import { MessageSquareText, Rocket, Lightbulb, ArrowRightCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  return (
    <main className="p-8 max-w-4xl mx-auto">
      <h1 className="text-4xl font-futuristic font-bold mb-10 tracking-wide text-white">
        Welcome to AI Coach
      </h1>

      <section className="rounded-xl bg-gradient-to-br from-cyan-600/30 via-primary/20 to-transparent border border-primary/40 p-7 flex items-center justify-between gap-6 shadow-xl hover:shadow-2xl mb-10 transition-shadow duration-300">
        <div className="flex items-center gap-5">
          <img
            src="/ai-34.png"
            alt="Get Started"
            className="w-20 h-20 object-contain drop-shadow-lg"
          />
          <div>
            <h2 className="text-2xl font-semibold text-white mb-2">Ready to level up?</h2>
            <p className="text-white/90 text-base max-w-md">
              Dive into real-world AI challenges—whether crafting sharp sales pitches, optimizing workflows, or sparking product innovation. Build your skills one prompt at a time.
            </p>
          </div>
        </div>
        <Link
          to="/challenges"
          className="inline-flex items-center gap-3 text-white bg-primary hover:bg-button-highlight px-6 py-3 rounded-xl text-lg font-semibold transition"
          aria-label="Start your challenges"
        >
          Let’s Go
          <ArrowRightCircle className="w-6 h-6" />
        </Link>
      </section>

      <section
        className="mb-14 p-7 rounded-xl bg-bg-panel bg-opacity-95 backdrop-blur-md border border-white/15 shadow-[0_0_15px_rgba(79,255,222,0.2)] text-white/95"
        aria-label="About AI Coach and prompting"
      >
        <article className="space-y-10 text-base leading-relaxed max-w-3xl">
          <div>
            <h3 className="text-xl font-semibold text-primary flex items-center gap-3 mb-3">
              <MessageSquareText className="w-6 h-6 text-primary" />
              What is AI Chat?
            </h3>
            <p>
              Think of AI Chat as your ultra-smart teammate, ready to brainstorm, write, and problem-solve instantly. Ask it to draft a process improvement plan, create social media captions, or explain a new product feature — and get clear, actionable answers fast.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-primary flex items-center gap-3 mb-3">
              <Lightbulb className="w-6 h-6 text-primary" />
              What’s Prompting?
            </h3>
            <p>
              Prompting is how you tell AI what you want. It’s like giving your teammate clear instructions — the better your prompt, the sharper and more useful the response.
            </p>
            <p className="mt-3 mb-3 text-white/80">
              <strong>Simple prompts you might try:</strong>
              <br />
              • "Outline a Kaizen event to reduce packaging waste." <br />
              • "Generate catchy Instagram captions for a new furniture collection."
            </p>

            <section className="rounded-xl bg-gradient-to-br from-cyan-600/30 via-primary/20 to-transparent border border-primary/40 p-5 max-w-2xl shadow-lg">
              <h4 className="text-primary font-semibold mb-1 text-lg">Pro Tip</h4>
              <ul className="list-disc list-inside text-white/90 space-y-0">
                <li><strong>Tone:</strong> Specify the style, like “professional” or “friendly.”</li>
                <li><strong>Audience:</strong> Mention who it’s for, such as “warehouse team” or “executives.”</li>
                <li><strong>Format:</strong> Request specific formatting like “bullet points” or “step-by-step plan.”</li>
              </ul>
              <p className="mt-1 text-white/80">
                Clear, precise prompts get you better AI-powered solutions.
              </p>
            </section>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-primary flex items-center gap-3 mb-3">
              <Rocket className="w-6 h-6 text-primary" />
              How to Use AI Coach
            </h3>
            <ol className="list-decimal list-inside space-y-0 ml-0 max-w-md">
              <li>Pick a challenge that fits your role or goal.</li>
              <li>Write your best prompt following the lesson’s objective.</li>
              <li>Submit and get instant feedback with a helpful score.</li>
              <li>Refine your prompts, repeat, and watch your skills grow!</li>
            </ol>
          </div>
        </article>
      </section>
    </main>
  );
};

export default Dashboard;
